'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@galapagos/design-system/web';
import { spacing } from '@galapagos/design-system/spacing';
import { PageColumn } from '@/components/page-column/PageColumn';
import '@/i18n';
import { useChunkRecovery } from './chunk-recovery';

/** Kept in step with the literal on the Button below; the analytics catalogue scanner needs the literal. */
export const APP_ERROR_RELOAD_ACTION_ID = 'app-error.reload-button.activate';

const columnStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.lg,
};

const copyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.s,
};

export interface AppErrorFallbackProps {
  error: unknown;
  'data-testid'?: string;
}

/**
 * Shared body of the root route and root layout error boundaries. A missing
 * chunk (a deploy replaced the static export under an open tab) reloads once
 * behind the recovery guard; everything else, or a second failure inside the
 * window, shows the localized fallback with a manual Reload.
 */
export function AppErrorFallback({
  error,
  'data-testid': testId = 'app-error',
}: AppErrorFallbackProps) {
  const { t } = useTranslation('common');
  const state = useChunkRecovery(error);

  if (state === 'reloading') {
    return (
      <PageColumn gap="lg" paddingBlock="xl">
        <div role="status" data-testid={`${testId}-recovering`}>
          <Text variant="b2Regular" color="global" value={t('appError.recovering')} />
        </div>
      </PageColumn>
    );
  }

  return (
    <PageColumn gap="lg" paddingBlock="xl">
      <div style={columnStyle} role="alert" data-testid={testId}>
        <div style={copyStyle}>
          <Text variant="h1Bold28" color="global" role="header" value={t('appError.title')} />
          <Text variant="b2Regular" color="global" value={t('appError.body')} />
        </div>
        <Button
          actionId="app-error.reload-button.activate"
          size="L"
          type="Primary"
          label={t('appError.reload')}
          onPress={() => window.location.reload()}
          data-testid={`${testId}-reload`}
        />
      </div>
    </PageColumn>
  );
}
